import { FunctionComponent, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../ui/dialog";
import { Workout } from "./workoutsColumns";
import { editorWorkoutToSavePayload } from "@/lib/utils/workoutSections";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { errorToast } from "@/lib/utils/ToastUtils";
import { toast } from "sonner";
import { store } from "@/lib/states/store";
import LoadingSpinner from "../LoadingSpinner";
import { trpc } from "@/lib/trpc/client";

interface SaveToLibraryDialogProps {
  workout: Workout | undefined;
  onClose: () => void;
}

const SaveToLibraryDialog: FunctionComponent<SaveToLibraryDialogProps> = ({
  workout,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const saveWorkoutMutation = trpc.workouts.save.useMutation();
  const utils = trpc.useUtils();

  useEffect(() => {
    setName(workout?.name ?? "");
  }, [workout]);

  const saveWorkout = async () => {
    const orgId = store.getState().session.org?.id;

    if (!workout || !orgId) {
      return;
    }

    setLoading(true);

    try {
      const data = await saveWorkoutMutation.mutateAsync({
        payload: editorWorkoutToSavePayload(workout, {
          orgId,
          name,
          isTemplate: true,
        }),
      });

      const savedWorkout =
        data && typeof data === "object" && "workout" in data
          ? (data.workout as { id?: string; created_at?: string } | undefined)
          : null;

      if (savedWorkout?.id) {
        await utils.workouts.list.invalidate();
      }
    } catch (error) {
      console.error("error adding workout", error);
      errorToast(error instanceof Error ? error : null);
      setLoading(false);
      return;
    }

    setLoading(false);
    toast.success("Workout saved to library");
    onClose();
  };

  return (
    <Dialog
      open={workout !== undefined}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      analyticsName="save_to_library_dialog"
    >
      <DialogContent className="flex flex-col">
        <DialogHeader>Save To Library</DialogHeader>

        <Label>Workout Name</Label>
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <DialogFooter>
          <Button onClick={onClose} variant={"ghost"}>
            Cancel
          </Button>
          <Button onClick={saveWorkout} disabled={loading}>
            {loading ? <LoadingSpinner /> : "Save To Library"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveToLibraryDialog;
